(() => {

  const CDN = 'https://cdn.jsdelivr.net/npm/';

  const PDF_MODULE =
    CDN + 'pdfjs-dist@4.10.38/build/pdf.min.mjs';

  const PDF_WORKER =
    CDN + 'pdfjs-dist@4.10.38/build/pdf.worker.min.mjs';

  const PAGE_FLIP =
    CDN + 'page-flip@2.0.7/dist/js/page-flip.browser.js';


  /* =========================================================
     SETTINGS
     ========================================================= */

  const INITIAL_PAGE_COUNT = 8;
  const BACKGROUND_BATCH_SIZE = 6;
  const RENDER_CONCURRENCY = 2;
  const SINGLE_PAGE_BREAKPOINT = 1366;


  /* =========================================================
     DEVICE DETECTION
     ========================================================= */

  function isIPadDevice() {

    const userAgent =
      navigator.userAgent || '';

    const platform =
      navigator.platform || '';

    const touchPoints =
      navigator.maxTouchPoints || 0;


    const classicIPad =
      /iPad/i.test(userAgent);


    const modernIPad =
      platform === 'MacIntel' &&
      touchPoints > 1;


    return (
      classicIPad ||
      modernIPad
    );

  }


  function useSinglePageViewer() {

    if (
      isIPadDevice()
    ) {
      return true;
    }


    return window.matchMedia(
      `(max-width: ${SINGLE_PAGE_BREAKPOINT}px)`
    ).matches;

  }


  /* =========================================================
     LOAD STPAGEFLIP
     ========================================================= */

  function loadPageFlipScript() {

    return new Promise(
      (
        resolve,
        reject
      ) => {

        if (
          window.St &&
          window.St.PageFlip
        ) {

          resolve();
          return;

        }


        const existing =
          document.querySelector(
            'script[data-vibrance-pageflip]'
          );


        if (existing) {

          existing.addEventListener(
            'load',
            resolve,
            {
              once: true
            }
          );


          existing.addEventListener(
            'error',
            reject,
            {
              once: true
            }
          );


          return;

        }


        const script =
          document.createElement(
            'script'
          );


        script.src =
          PAGE_FLIP;


        script.async =
          true;


        script.dataset.vibrancePageflip =
          'true';


        script.onload =
          resolve;


        script.onerror =
          () => {

            reject(
              new Error(
                'The page-turn library could not be loaded.'
              )
            );

          };


        document.head.appendChild(
          script
        );

      }
    );

  }


  /* =========================================================
     CANVAS -> BLOB
     ========================================================= */

  function canvasToBlob(canvas) {

    return new Promise(
      resolve => {

        canvas.toBlob(
          blob => {

            if (blob) {

              resolve(blob);
              return;

            }


            canvas.toBlob(
              fallback =>
                resolve(fallback),
              'image/jpeg',
              0.94
            );

          },
          'image/webp',
          0.94
        );

      }
    );

  }


  /* =========================================================
     PLACEHOLDER
     ========================================================= */

  function createPlaceholder() {

    const svg =
      '<svg xmlns="http://www.w3.org/2000/svg" ' +
      'width="1200" height="1600" viewBox="0 0 1200 1600">' +
      '<rect width="1200" height="1600" fill="#ffffff"/>' +
      '</svg>';


    return (
      'data:image/svg+xml;charset=utf-8,' +
      encodeURIComponent(svg)
    );

  }


  /* =========================================================
     PRE-DECODE IMAGE
     ========================================================= */

  async function decodeImage(url) {

    if (!url) {
      return;
    }


    const image =
      new Image();


    image.src =
      url;


    try {

      if (
        typeof image.decode ===
        'function'
      ) {

        await image.decode();
        return;

      }

    }

    catch (error) {}


    if (image.complete) {
      return;
    }


    await new Promise(
      resolve => {

        image.onload =
          resolve;

        image.onerror =
          resolve;

      }
    );

  }


  /* =========================================================
     INITIALISE
     ========================================================= */

  async function initLookbook(root) {

    if (
      !root ||
      root.dataset.initialized === 'true'
    ) {
      return;
    }


    root.dataset.initialized =
      'true';


    const singlePageMode =
      useSinglePageViewer();


    root.classList.toggle(
      'vibrance-single-page',
      singlePageMode
    );


    root.classList.toggle(
      'vibrance-spread-page',
      !singlePageMode
    );


    const pdfUrl =
      root.dataset.pdfUrl;


    const loading =
      root.querySelector(
        '.vibrance-reader__loading'
      );


    const loadingStatus =
      root.querySelector(
        '.vibrance-reader__loading-status'
      );


    const progressBar =
      root.querySelector(
        '.vibrance-reader__progress-bar'
      );


    const stage =
      root.querySelector(
        '.vibrance-reader__stage'
      );


    const bookElement =
      root.querySelector(
        '.vibrance-reader__book'
      );


    const controls =
      root.querySelector(
        '.vibrance-reader__controls'
      );


    const hint =
      root.querySelector(
        '.vibrance-reader__hint'
      );


    const previousButton =
      root.querySelector(
        '.vibrance-reader__prev'
      );


    const nextButton =
      root.querySelector(
        '.vibrance-reader__next'
      );


    const currentElement =
      root.querySelector(
        '.vibrance-reader__current'
      );


    const totalElement =
      root.querySelector(
        '.vibrance-reader__total'
      );


    const jumpForm =
      root.querySelector(
        '.vibrance-reader__jump'
      );


    const jumpInput =
      root.querySelector(
        '.vibrance-reader__jump-input'
      );


    let pdfDocument = null;
    let pageFlip = null;
    let pageCount = 0;
    let pageAspectRatio = 1 / 1.414;

    let currentPageIndex = 0;

    let desktopBookBusy = false;
    let desktopRefreshNeeded = false;

    let backgroundLoading = false;

    let singleAnimating = false;
    let touchStartX = 0;

    let singleViewer = null;
    let singleUnderImage = null;
    let singleCurrentSheet = null;
    let singleFrontImage = null;
    let singleBackImage = null;
    let singleShade = null;


    const pageImages = [];
    const objectUrls = [];

    const renderedPages =
      new Set();

    const placeholder =
      createPlaceholder();


    /* =======================================================
       LOADING UI
       ======================================================= */

    function updateProgress(
      current,
      total,
      message
    ) {

      const percent =
        total > 0
          ? Math.round(
              current /
              total *
              100
            )
          : 0;


      if (progressBar) {

        progressBar.style.width =
          percent + '%';

      }


      if (loadingStatus) {

        loadingStatus.textContent =
          message;

      }

    }


    function showError(error) {

      console.error(
        'VIBRANCE Lookbook:',
        error
      );


      if (loading) {

        loading.innerHTML =
          '<div class="vibrance-reader__loading-card">' +
            '<strong class="vibrance-reader__loading-title">' +
              'Lookbook could not be loaded.' +
            '</strong>' +
            '<span class="vibrance-reader__loading-status">' +
              (
                error &&
                error.message
                  ? error.message
                  : 'Please check the PDF file.'
              ) +
            '</span>' +
          '</div>';

      }


      if (stage) {

        stage.hidden =
          true;

      }


      if (controls) {

        controls.hidden =
          true;

      }


      if (hint) {

        hint.hidden =
          true;

      }

    }


    /* =======================================================
       RENDER QUALITY
       ======================================================= */

    function getTargetWidth() {

      const screenWidth =
        window.innerWidth;


      const pixelRatio =
        Math.min(
          window.devicePixelRatio || 1,
          2
        );


      if (singlePageMode) {

        return Math.round(
          Math.min(
            1800,
            Math.max(
              1200,
              screenWidth *
              pixelRatio *
              1.30
            )
          )
        );

      }


      return Math.round(
        Math.min(
          1600,
          Math.max(
            1100,
            screenWidth *
            pixelRatio *
            0.70
          )
        )
      );

    }


    /* =======================================================
       RENDER PDF PAGE
       ======================================================= */

    async function renderPageImage(
      pageNumber
    ) {

      if (
        renderedPages.has(
          pageNumber
        )
      ) {

        return pageImages[
          pageNumber - 1
        ];

      }


      const page =
        await pdfDocument.getPage(
          pageNumber
        );


      const baseViewport =
        page.getViewport({
          scale: 1
        });


      const targetWidth =
        getTargetWidth();


      const scale =
        targetWidth /
        baseViewport.width;


      const viewport =
        page.getViewport({
          scale
        });


      const canvas =
        document.createElement(
          'canvas'
        );


      canvas.width =
        Math.ceil(
          viewport.width
        );


      canvas.height =
        Math.ceil(
          viewport.height
        );


      const context =
        canvas.getContext(
          '2d',
          {
            alpha: false
          }
        );


      await page.render({

        canvasContext:
          context,

        viewport,

        background:
          '#ffffff'

      }).promise;


      const blob =
        await canvasToBlob(
          canvas
        );


      if (!blob) {

        throw new Error(
          'Page ' +
          pageNumber +
          ' could not be prepared.'
        );

      }


      const objectUrl =
        URL.createObjectURL(
          blob
        );


      objectUrls.push(
        objectUrl
      );


      pageImages[
        pageNumber - 1
      ] =
        objectUrl;


      renderedPages.add(
        pageNumber
      );


      canvas.width =
        1;

      canvas.height =
        1;


      return objectUrl;

    }


    /* =======================================================
       ENSURE PAGE READY
       ======================================================= */

    async function ensurePage(
      pageIndex
    ) {

      const safeIndex =
        Math.max(
          0,
          Math.min(
            pageIndex,
            pageCount - 1
          )
        );


      const pageNumber =
        safeIndex + 1;


      if (
        !renderedPages.has(
          pageNumber
        )
      ) {

        await renderPageImage(
          pageNumber
        );

      }


      const url =
        pageImages[
          safeIndex
        ];


      await decodeImage(
        url
      );


      return url;

    }


    /* =======================================================
       RENDER PAGE LIST
       ======================================================= */

    async function renderPageList(
      pageNumbers,
      progressCallback
    ) {

      let cursor = 0;


      async function worker() {

        while (
          cursor <
          pageNumbers.length
        ) {

          const index =
            cursor++;


          const pageNumber =
            pageNumbers[
              index
            ];


          await renderPageImage(
            pageNumber
          );


          if (progressCallback) {

            progressCallback(
              pageNumber
            );

          }


          await new Promise(
            resolve =>
              requestAnimationFrame(
                resolve
              )
          );

        }

      }


      const workers = [];


      const count =
        Math.min(
          RENDER_CONCURRENCY,
          pageNumbers.length
        );


      for (
        let index = 0;
        index < count;
        index++
      ) {

        workers.push(
          worker()
        );

      }


      await Promise.all(
        workers
      );

    }


    /* =======================================================
       CONTROLS
       ======================================================= */

    function updateControls(
      pageIndex
    ) {

      const safeIndex =
        Math.max(
          0,
          Math.min(
            Number(pageIndex) || 0,
            pageCount - 1
          )
        );


      currentPageIndex =
        safeIndex;


      if (currentElement) {

        currentElement.textContent =
          safeIndex + 1;

      }


      if (jumpInput) {

        jumpInput.value =
          safeIndex + 1;

      }


      if (previousButton) {

        previousButton.disabled =
          safeIndex <= 0;

      }


      if (nextButton) {

        nextButton.disabled =
          safeIndex >=
          pageCount - 1;

      }


      if (hint) {

        hint.textContent =
          singlePageMode
            ? 'Swipe, drag, or use the arrows to turn the page.'
            : 'Drag a page, swipe, use ← →, or jump directly to a page.';

      }

    }


    /* =======================================================
       CREATE SINGLE PAGE VIEWER
       ======================================================= */

    async function createSinglePageViewer() {

      bookElement.innerHTML =
        '';


      singleViewer =
        document.createElement(
          'div'
        );


      singleViewer.className =
        'vibrance-single-viewer';


      singleViewer.style.aspectRatio =
        pageAspectRatio;


      /*
        Underlying next page
      */

      singleUnderImage =
        document.createElement(
          'img'
        );


      singleUnderImage.className =
        'vibrance-single-under-image';


      /*
        Turning sheet
      */

      singleCurrentSheet =
        document.createElement(
          'div'
        );


      singleCurrentSheet.className =
        'vibrance-single-sheet';


      /*
        Front
      */

      const frontFace =
        document.createElement(
          'div'
        );


      frontFace.className =
        'vibrance-single-face vibrance-single-front';


      singleFrontImage =
        document.createElement(
          'img'
        );


      frontFace.appendChild(
        singleFrontImage
      );


      /*
        Back
      */

      const backFace =
        document.createElement(
          'div'
        );


      backFace.className =
        'vibrance-single-face vibrance-single-back';


      singleBackImage =
        document.createElement(
          'img'
        );


      backFace.appendChild(
        singleBackImage
      );


      /*
        Moving shadow
      */

      singleShade =
        document.createElement(
          'div'
        );


      singleShade.className =
        'vibrance-single-turn-shadow';


      singleCurrentSheet.appendChild(
        frontFace
      );


      singleCurrentSheet.appendChild(
        backFace
      );


      singleCurrentSheet.appendChild(
        singleShade
      );


      singleViewer.appendChild(
        singleUnderImage
      );


      singleViewer.appendChild(
        singleCurrentSheet
      );


      bookElement.appendChild(
        singleViewer
      );


      const firstUrl =
        await ensurePage(
          0
        );


      const nextUrl =
        pageCount > 1
          ? await ensurePage(1)
          : firstUrl;


      singleFrontImage.src =
        firstUrl;


      singleBackImage.src =
        nextUrl;


      singleUnderImage.src =
        nextUrl;


      updateControls(
        0
      );


      /* =====================================================
         SWIPE
         ===================================================== */

      singleViewer.addEventListener(
        'touchstart',
        event => {

          if (
            !event.changedTouches ||
            !event.changedTouches.length
          ) {
            return;
          }


          touchStartX =
            event.changedTouches[
              0
            ].clientX;

        },
        {
          passive: true
        }
      );


      singleViewer.addEventListener(
        'touchend',
        event => {

          if (
            !event.changedTouches ||
            !event.changedTouches.length
          ) {
            return;
          }


          const endX =
            event.changedTouches[
              0
            ].clientX;


          const difference =
            endX -
            touchStartX;


          if (
            Math.abs(
              difference
            ) < 45
          ) {
            return;
          }


          if (
            difference < 0
          ) {

            goToSinglePage(
              currentPageIndex + 1,
              'next'
            );

          } else {

            goToSinglePage(
              currentPageIndex - 1,
              'prev'
            );

          }

        },
        {
          passive: true
        }
      );

    }


    /* =======================================================
       SINGLE PAGE CURL TURN
       ======================================================= */

    async function goToSinglePage(
      targetIndex,
      direction = 'next',
      animate = true
    ) {

      if (
        singleAnimating
      ) {
        return;
      }


      const safeTarget =
        Math.max(
          0,
          Math.min(
            targetIndex,
            pageCount - 1
          )
        );


      if (
        safeTarget ===
        currentPageIndex
      ) {
        return;
      }


      singleAnimating =
        true;


      try {

        const currentUrl =
          await ensurePage(
            currentPageIndex
          );


        const targetUrl =
          await ensurePage(
            safeTarget
          );


        singleCurrentSheet.classList.remove(
          'turn-next',
          'turn-prev'
        );


        /*
          Jump
        */

        if (!animate) {

          singleFrontImage.src =
            targetUrl;


          singleBackImage.src =
            targetUrl;


          singleUnderImage.src =
            targetUrl;


          updateControls(
            safeTarget
          );


          return;

        }


        /*
          FORWARD
        */

        if (
          direction === 'next'
        ) {

          singleFrontImage.src =
            currentUrl;


          singleBackImage.src =
            targetUrl;


          singleUnderImage.src =
            targetUrl;


          await decodeImage(
            targetUrl
          );


          void singleCurrentSheet.offsetWidth;


          singleCurrentSheet.classList.add(
            'turn-next'
          );

        }


        /*
          BACKWARD

          Target page becomes the turning sheet.
        */

        else {

          singleFrontImage.src =
            targetUrl;


          singleBackImage.src =
            currentUrl;


          singleUnderImage.src =
            currentUrl;


          await decodeImage(
            targetUrl
          );


          singleCurrentSheet.classList.add(
            'prepare-prev'
          );


          void singleCurrentSheet.offsetWidth;


          singleCurrentSheet.classList.remove(
            'prepare-prev'
          );


          singleCurrentSheet.classList.add(
            'turn-prev'
          );

        }


        await new Promise(
          resolve =>
            setTimeout(
              resolve,
              680
            )
        );


        /*
          Reset sheet to target page.
        */

        singleCurrentSheet.classList.remove(
          'turn-next',
          'turn-prev'
        );


        singleFrontImage.src =
          targetUrl;


        const nextNeighbour =
          safeTarget + 1 <
          pageCount
            ? await ensurePage(
                safeTarget + 1
              )
            : targetUrl;


        singleBackImage.src =
          nextNeighbour;


        singleUnderImage.src =
          nextNeighbour;


        updateControls(
          safeTarget
        );


        /*
          Preload nearby pages.
        */

        [
          safeTarget - 1,
          safeTarget + 1,
          safeTarget + 2
        ].forEach(
          index => {

            if (
              index >= 0 &&
              index < pageCount
            ) {

              ensurePage(
                index
              ).catch(
                () => {}
              );

            }

          }
        );

      }

      finally {

        singleAnimating =
          false;

      }

    }


    /* =======================================================
       DESKTOP BOOK
       ======================================================= */

    async function createDesktopBook() {

      if (
        !window.St ||
        !window.St.PageFlip
      ) {

        throw new Error(
          'The page-turn engine did not initialise.'
        );

      }


      const firstPage =
        await pdfDocument.getPage(
          1
        );


      const viewport =
        firstPage.getViewport({
          scale: 1
        });


      const ratio =
        viewport.height /
        viewport.width;


      const baseWidth =
        820;


      const baseHeight =
        Math.round(
          baseWidth *
          ratio
        );


      pageFlip =
        new window.St.PageFlip(
          bookElement,
          {

            width:
              baseWidth,

            height:
              baseHeight,

            size:
              'stretch',

            minWidth:
              280,

            maxWidth:
              900,

            minHeight:
              Math.round(
                280 *
                ratio
              ),

            maxHeight:
              Math.round(
                900 *
                ratio
              ),

            drawShadow:
              true,

            flippingTime:
              720,

            usePortrait:
              false,

            startPage:
              0,

            autoSize:
              true,

            maxShadowOpacity:
              0.30,

            showCover:
              true,

            mobileScrollSupport:
              true,

            swipeDistance:
              24,

            useMouseEvents:
              true,

            disableFlipByClick:
              false

          }
        );


      pageFlip.on(
        'flip',
        event => {

          updateControls(
            event.data
          );

        }
      );


      pageFlip.on(
        'init',
        event => {

          const index =
            event &&
            event.data &&
            typeof event.data.page !==
              'undefined'
              ? event.data.page
              : 0;


          updateControls(
            index
          );

        }
      );


      pageFlip.on(
        'changeState',
        event => {

          desktopBookBusy =
            event.data !==
            'read';


          if (
            !desktopBookBusy &&
            desktopRefreshNeeded
          ) {

            refreshDesktopBook();

          }

        }
      );


      pageFlip.loadFromImages(
        pageImages.slice()
      );

    }


    /* =======================================================
       REFRESH DESKTOP BOOK
       ======================================================= */

    function refreshDesktopBook() {

      if (
        singlePageMode ||
        !pageFlip
      ) {
        return;
      }


      if (
        desktopBookBusy
      ) {

        desktopRefreshNeeded =
          true;

        return;

      }


      desktopRefreshNeeded =
        false;


      const currentPage =
        pageFlip.getCurrentPageIndex();


      try {

        pageFlip.updateFromImages(
          pageImages.slice()
        );

      }

      catch (error) {}


      requestAnimationFrame(
        () => {

          if (!pageFlip) {
            return;
          }


          try {

            pageFlip.turnToPage(
              currentPage
            );

          }

          catch (error) {}


          updateControls(
            currentPage
          );

        }
      );

    }


    /* =======================================================
       INITIAL PAGES
       ======================================================= */

    async function prepareInitialPages() {

      const initialCount =
        Math.min(
          INITIAL_PAGE_COUNT,
          pageCount
        );


      const pages = [];


      for (
        let page = 1;
        page <= initialCount;
        page++
      ) {

        pages.push(
          page
        );

      }


      let complete = 0;


      await renderPageList(
        pages,
        () => {

          complete++;


          updateProgress(
            complete,
            initialCount,
            'Preparing lookbook… ' +
            complete +
            ' of ' +
            initialCount
          );

        }
      );


      const decodeCount =
        Math.min(
          4,
          initialCount
        );


      for (
        let index = 0;
        index < decodeCount;
        index++
      ) {

        await decodeImage(
          pageImages[
            index
          ]
        );

      }

    }


    /* =======================================================
       BACKGROUND LOADING
       ======================================================= */

    async function prepareRemainingPages() {

      if (
        backgroundLoading
      ) {
        return;
      }


      backgroundLoading =
        true;


      const remaining = [];


      for (
        let page = 1;
        page <= pageCount;
        page++
      ) {

        if (
          !renderedPages.has(
            page
          )
        ) {

          remaining.push(
            page
          );

        }

      }


      for (
        let start = 0;
        start < remaining.length;
        start += BACKGROUND_BATCH_SIZE
      ) {

        const batch =
          remaining.slice(
            start,
            start +
            BACKGROUND_BATCH_SIZE
          );


        await renderPageList(
          batch
        );


        if (!singlePageMode) {

          refreshDesktopBook();

        }


        await new Promise(
          resolve =>
            setTimeout(
              resolve,
              60
            )
        );

      }


      backgroundLoading =
        false;

    }


    /* =======================================================
       PREVIOUS
       ======================================================= */

    if (previousButton) {

      previousButton.addEventListener(
        'click',
        () => {

          if (singlePageMode) {

            goToSinglePage(
              currentPageIndex - 1,
              'prev'
            );

            return;

          }


          if (
            !pageFlip ||
            previousButton.disabled
          ) {
            return;
          }


          pageFlip.flipPrev(
            'bottom'
          );

        }
      );

    }


    /* =======================================================
       NEXT
       ======================================================= */

    if (nextButton) {

      nextButton.addEventListener(
        'click',
        () => {

          if (singlePageMode) {

            goToSinglePage(
              currentPageIndex + 1,
              'next'
            );

            return;

          }


          if (
            !pageFlip ||
            nextButton.disabled
          ) {
            return;
          }


          pageFlip.flipNext(
            'bottom'
          );

        }
      );

    }


    /* =======================================================
       PAGE JUMP
       ======================================================= */

    if (
      jumpForm &&
      jumpInput
    ) {

      jumpForm.addEventListener(
        'submit',
        event => {

          event.preventDefault();


          let requested =
            parseInt(
              jumpInput.value,
              10
            );


          if (
            Number.isNaN(
              requested
            )
          ) {

            jumpInput.value =
              currentPageIndex + 1;

            return;

          }


          requested =
            Math.max(
              1,
              Math.min(
                requested,
                pageCount
              )
            );


          const index =
            requested - 1;


          if (singlePageMode) {

            const direction =
              index <
              currentPageIndex
                ? 'prev'
                : 'next';


            goToSinglePage(
              index,
              direction,
              false
            );


            return;

          }


          if (!pageFlip) {
            return;
          }


          if (
            !renderedPages.has(
              requested
            )
          ) {

            renderPageImage(
              requested
            )
            .then(
              () => {

                refreshDesktopBook();


                setTimeout(
                  () => {

                    if (!pageFlip) {
                      return;
                    }


                    pageFlip.turnToPage(
                      index
                    );


                    updateControls(
                      index
                    );

                  },
                  80
                );

              }
            );


            return;

          }


          pageFlip.turnToPage(
            index
          );


          updateControls(
            index
          );

        }
      );

    }


    /* =======================================================
       KEYBOARD
       ======================================================= */

    document.addEventListener(
      'keydown',
      event => {

        const active =
          document.activeElement;


        const typing =
          active &&
          (
            active.tagName === 'INPUT' ||
            active.tagName === 'TEXTAREA' ||
            active.tagName === 'SELECT' ||
            active.isContentEditable
          );


        if (typing) {
          return;
        }


        const rect =
          stage.getBoundingClientRect();


        const visible =
          rect.bottom > 0 &&
          rect.top <
          window.innerHeight;


        if (!visible) {
          return;
        }


        if (
          event.key === 'ArrowRight'
        ) {

          event.preventDefault();


          if (
            nextButton &&
            !nextButton.disabled
          ) {

            nextButton.click();

          }

        }


        if (
          event.key === 'ArrowLeft'
        ) {

          event.preventDefault();


          if (
            previousButton &&
            !previousButton.disabled
          ) {

            previousButton.click();

          }

        }

      }
    );


    /* =======================================================
       START
       ======================================================= */

    try {

      if (!pdfUrl) {

        throw new Error(
          'Add your Shopify PDF URL in the section settings.'
        );

      }


      updateProgress(
        0,
        INITIAL_PAGE_COUNT,
        'Loading viewer…'
      );


      let pdfjs;


      if (singlePageMode) {

        pdfjs =
          await import(
            PDF_MODULE
          );

      } else {

        const results =
          await Promise.all(
            [

              import(
                PDF_MODULE
              ),

              loadPageFlipScript()

            ]
          );


        pdfjs =
          results[0];

      }


      pdfjs
        .GlobalWorkerOptions
        .workerSrc =
          PDF_WORKER;


      updateProgress(
        0,
        INITIAL_PAGE_COUNT,
        'Opening PDF…'
      );


      pdfDocument =
        await pdfjs
          .getDocument({
            url: pdfUrl
          })
          .promise;


      pageCount =
        pdfDocument.numPages;


      if (totalElement) {

        totalElement.textContent =
          pageCount;

      }


      if (jumpInput) {

        jumpInput.max =
          pageCount;

      }


      const firstPdfPage =
        await pdfDocument.getPage(
          1
        );


      const firstViewport =
        firstPdfPage.getViewport({
          scale: 1
        });


      pageAspectRatio =
        firstViewport.width /
        firstViewport.height;


      for (
        let index = 0;
        index < pageCount;
        index++
      ) {

        pageImages.push(
          placeholder
        );

      }


      await prepareInitialPages();


      updateProgress(
        Math.min(
          INITIAL_PAGE_COUNT,
          pageCount
        ),
        Math.min(
          INITIAL_PAGE_COUNT,
          pageCount
        ),
        'Opening lookbook…'
      );


      stage.hidden =
        false;


      if (singlePageMode) {

        await createSinglePageViewer();

      } else {

        await createDesktopBook();

      }


      loading.hidden =
        true;


      controls.hidden =
        false;


      hint.hidden =
        false;


      updateControls(
        0
      );


      setTimeout(
        () => {

          prepareRemainingPages()
            .catch(
              error => {

                console.error(
                  'VIBRANCE background loading:',
                  error
                );

              }
            );

        },
        350
      );

    }

    catch (error) {

      showError(
        error
      );

    }


    /* =======================================================
       CLEANUP
       ======================================================= */

    window.addEventListener(
      'pagehide',
      () => {

        objectUrls.forEach(
          url => {

            try {

              URL.revokeObjectURL(
                url
              );

            }

            catch (error) {}

          }
        );

      },
      {
        once: true
      }
    );

  }


  /* =========================================================
     INIT ALL
     ========================================================= */

  function initAll() {

    document
      .querySelectorAll(
        '.vibrance-experience[data-pdf-url]'
      )
      .forEach(
        root => {

          initLookbook(
            root
          );

        }
      );

  }


  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initAll,
      {
        once: true
      }
    );

  } else {

    initAll();

  }

})();